import React, { useCallback, useState } from 'react';
import '../microbar.scss';
import CustomIcon, { IconType } from '../../custom-icon/custom-icon';
import { useNavigate } from 'src/utils/use-navigate';
import moment from 'moment/moment';
import { useStores } from '../../../store';
import useCustomModal from '../../custom-modal/custom-modal';
import { MAINTENANCE_ALERT } from 'src/utils/constants';
import { EventType } from '../../../../dtos/analytics.dto';
import { observer } from 'mobx-react-lite';
import { SmartPolicyTaskStatus } from '@src/utils/enums';
import CancelSmartModal from '@src/components/shared/cancel-smart-modal/cancel-smart-modal';
import { ConnectionDto } from '@src/dtos/customer.dto';

interface DraftExistAlertProps {
    pathToDraftVersion: string;
}
export const DraftExistAlert: React.FC<DraftExistAlertProps> = ({pathToDraftVersion}) => {
    const navigate = useNavigate();

    const goToDraftVersion = useCallback(() => {
        if (pathToDraftVersion) {
            navigate(pathToDraftVersion);
        }
    }, [navigate, pathToDraftVersion]);

    return <>
        <span className="message">Draft exists </span>
        <button type="button" className={'action'} onClick={goToDraftVersion}>Edit draft</button>
    </>;
};

export const ErrorAlert: React.FC = () => {
    return <><CustomIcon type={IconType.RED_CLEAR} size={16}/> Unable to save your changes. Try again or
        check your network connection.</>;
};

export const MicrobarAlert: React.FC = () => {
    return <><CustomIcon type={IconType.View_Icon} size={16}/> View only</>;
};

export const MaintenanceAlert: React.FC = () => {
    return <span>{MAINTENANCE_ALERT}</span>;
};

export const ScheduledVersionAlert: React.FC = observer(() => {
    const { customerStore } = useStores();
    const navigate = useNavigate();
    const sidMetadata = customerStore.sidMetadata;
    if (!sidMetadata) return null;

    const text = `Apply policy scheduled for ${moment(sidMetadata.scheduledDate).format('MMM DD, YYYY hh:mm A')}`;

    if (customerStore.isSourceScheduledVersion) {
        return <span>{text}</span>;
    } else {
        const connection = customerStore.getCustomerServiceData(sidMetadata.sourceSid);
        const serviceName = connection?.adiCircuitId ?? connection?.serviceName ?? sidMetadata.sourceSid;

        return (
            <>
                <span>{text}, from source connection </span>
                <button type="button" className="action"
                    onClick={() => navigate(`/${customerStore.customerId}/${sidMetadata.sourceSid}/dashboard`)}>
                    {serviceName}
                </button>
            </>
        );
    }
});
const useDeactivatedVersion = () => {
    const [showModal, , contextHolder] = useCustomModal();
    const {fwVersionStore} = useStores();
    const navigate = useNavigate();

    const goToLatestVersion = useCallback(() => {
        if (fwVersionStore.pathToLatestVersion) {
            navigate(fwVersionStore.pathToLatestVersion);
        }
    }, [navigate, fwVersionStore.pathToLatestVersion]);

    const reactivateVersion = () => {
        const draftNote = 'Any draft policy will be deleted.';
        showModal({
            width: 560,
            title: 'Reactivate Dynamic Defense',
            body: () => <div className="modal-deactivation-version-message">
                Reactivate Dynamic Defense will restore the protection of the previous policy. {draftNote}
            </div>,
            okText: 'Reactivate',
            cancelText: 'Cancel',
            onOk: async () => {
                fwVersionStore.sendGeneralEvent(EventType.LinkClicked, {
                    button: 'Reactivate',
                    location: 'reactivationModal'
                });
                await fwVersionStore.deactivateReactivateVersion(false);
                goToLatestVersion();
            },
            onCancel: () => {
                fwVersionStore.sendGeneralEvent(EventType.LinkClicked, {
                    button: 'cancel',
                    location: 'reactivationModal',
                });
            }
        });
    };

    return { reactivateVersion, contextHolder };
};

export const DeactivatedVersionAlert: React.FC = () => {

    const { reactivateVersion, contextHolder } = useDeactivatedVersion();
    return (
        <>
            {contextHolder}
            <span>Dynamic Defense is deactivated.</span>
            <button type="button" className={'action'} onClick={reactivateVersion}>Reactivate here</button>
        </>
    );
};

const useCancelPolicyMode = (draftId?: string) => {
    const { customerStore, fwVersionStore } = useStores();
    const [showModal, , contextHolder] = useCustomModal();
    const navigate = useNavigate();

    const cancelPolicyMode = () => {
        const content = draftId ? 'Your draft changes will be deleted.'
            : 'Return to the policy selection page ?';
        showModal({
            wrapClassName: 'delete-modal-wrapper',
            width: 500,
            title: 'Back to policy selection',
            body: () => <div className="modal-commit-message">{content}</div>,
            okText: draftId ? 'Delete & Continue' : 'Continue',
            cancelText: 'Cancel',
            onOk: async () => {
                if (draftId) {
                    fwVersionStore.sendGeneralEvent(EventType.LinkClicked, { button: 'delete' });
                }
                await customerStore.rollbackPolicyMode([customerStore.serviceId]);
                navigate(`/${customerStore.customerId}/wizard`, {
                    state: {
                        initialStep: 2,
                        initialData: JSON.stringify({ connection: customerStore.selectedLocation }),
                    }
                });
            },
            closable: false
        });
    };

    return { cancelPolicyMode, contextHolder };
};


export const SmartAlert: React.FC = observer(() => {
    const { customerStore } = useStores();
    const [showCancelModal, setShowCancelModal] = useState(false);

    const handleCancelSmart = () => {
        customerStore.sendGeneralEvent(EventType.LinkClicked, {
            button: 'cancelSmartProtection',
            location: 'smartProtectionMicroBar'
        });
        setShowCancelModal(true);
    };

    const taskStatus = customerStore.selectedLocation?.smartPolicyTaskStatus;

    if (taskStatus === SmartPolicyTaskStatus.PENDING) {
        // Soaking period — show estimated completion date/time and cancel link
        const selectedLocation = customerStore.selectedLocation as ConnectionDto;
        const networkAnalysisDaysLeft = selectedLocation?.networkAnalysisDaysLeft ?? 0;
        let timeText =  networkAnalysisDaysLeft > 1 ? `in ${networkAnalysisDaysLeft} days` : 'in 1 day';
        if (!networkAnalysisDaysLeft) {
            timeText = 'today';
        }

        return (
            <div className="smart-policy-alert">
                <div>We are monitoring your network traffic. Analysis will be completed {timeText}.</div>
                {networkAnalysisDaysLeft > 0 && <button type="button" className="cancel-smart-policy" onClick={handleCancelSmart}>Cancel Smart Protection</button>}
                {customerStore.selectedLocation &&
                    <CancelSmartModal visible={showCancelModal} onClose={() => setShowCancelModal(false)}
                                              connectionId={customerStore.selectedLocation.serviceId} showCancelAll={false} />}
            </div>
        );
    } else if (taskStatus === SmartPolicyTaskStatus.IN_PROGRESS) {
        // Draft creation in progress — no cancel link
        return <span>We have started building your policy. This might take a few minutes.</span>;
    }

    return null;
});

export const BackPolicyAlert: React.FC = () => {
    const { fwVersionStore } = useStores();
    const draftId = fwVersionStore.draftVersion?.externalId;
    const { cancelPolicyMode, contextHolder } = useCancelPolicyMode(draftId);

    return <>
        {contextHolder}
        <div className={`policy-selection-alert ${!fwVersionStore.draftVersion ? 'no-draft': ''}`}>
            {fwVersionStore.draftVersion && <div>Your policy is pending your review. Click the Apply draft button to Apply Policy.</div>}
            <button type="button" className="back-policy-selection" onClick={cancelPolicyMode}>Back to policy selection</button>
        </div>
    </>;
};